'use client'

/**
 * Pagination — FO-155: app-brede, gedeelde paginering-balk.
 *
 * Opbouw: `[Eerste] [Vorige] Pagina {kiezer} van {totaal} [Volgende] [Laatste]`
 * plus een optionele itemtelling ("312 artikelen"). De kiezer is een compacte,
 * typebare pagina-picker (combobox op desktop, native `<select>`/number-input op
 * touch) zodat je in één handeling naar pagina 24 springt in plaats van 20x te
 * klikken.
 *
 * De navigatie-logica is niet ingebouwd: geef `onPageChange` mee (bestaand
 * `navigatePage`/`goToPage` van de lijst, dat de URL + filters behoudt). Zonder
 * callback navigeert het component zelf via de URL-`page`-param (huidige query
 * blijft behouden) - handig voor server-component-lijsten (klant-app).
 *
 * i18n: `labels` overschrijft de NL-defaults (injecteerbaar voor next-intl in de
 * klant-app). Rendert niets bij <= 1 pagina (§6). Streep-tokens, tap-targets
 * >= 44px op touch, `nav[aria-label]` + aria op de knoppen/kiezer.
 */

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import {
  Check,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { Command } from 'cmdk'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { cn } from '../lib/utils'

// ── Labels (i18n) ───────────────────────────────────────────────────────────

export interface PaginationLabels {
  /** aria-label op de `<nav>`. */
  nav: string
  previous: string
  next: string
  first: string
  last: string
  /** Prefix voor de kiezer: "Pagina". */
  page: string
  /** Verbinding tussen kiezer en totaal: "van". */
  of: string
  /** aria-label op de pagina-kiezer. */
  goToPage: string
  /** Label bij de optionele pageSize-kiezer. */
  perPage: string
}

export const DEFAULT_PAGINATION_LABELS: PaginationLabels = {
  nav: 'Paginering',
  previous: 'Vorige',
  next: 'Volgende',
  first: 'Eerste pagina',
  last: 'Laatste pagina',
  page: 'Pagina',
  of: 'van',
  goToPage: 'Ga naar pagina',
  perPage: 'per pagina',
}

/** Merge de NL-defaults met de meegegeven (i18n-)labels. Pure, testbaar. */
export function resolvePaginationLabels(labels?: Partial<PaginationLabels>): PaginationLabels {
  return labels ? { ...DEFAULT_PAGINATION_LABELS, ...labels } : DEFAULT_PAGINATION_LABELS
}

// ── Pure paginering-afgeleiden (testbaar zonder render) ──────────────────────

/** Boven dit aantal pagina's op touch een number-input i.p.v. een lange `<select>`. */
export const MOBILE_SELECT_MAX = 100
/** Boven dit aantal pagina's rendert de desktop-kiezer een venster i.p.v. alle opties. */
export const LAZY_OPTIONS_THRESHOLD = 500

/**
 * Leidt het aantal pagina's af uit óf `totalPages` óf (`total` + `pageSize`).
 * Nooit < 1; deelt nooit door nul (bogus `pageSize` -> `total`).
 */
export function resolveTotalPages(opts: {
  total?: number
  totalPages?: number
  pageSize?: number
}): number {
  if (opts.totalPages != null) return Math.max(1, Math.trunc(opts.totalPages))
  const total = opts.total ?? 0
  const size = Math.max(1, opts.pageSize ?? 1)
  return Math.max(1, Math.ceil(total / size))
}

/**
 * Afgeleide staat: verberg-regel, geklemde huidige pagina en de disabled-states
 * van Vorige/Volgende. Los geëxporteerd zodat de off-by-one en de "verberg bij 1
 * pagina"-regel zonder render te unit-testen zijn.
 */
export function paginationState(opts: {
  total?: number
  totalPages?: number
  page: number
  pageSize?: number
}) {
  const totalPages = resolveTotalPages(opts)
  const current = Math.min(Math.max(1, Math.trunc(opts.page || 1)), totalPages)
  return {
    totalPages,
    current,
    show: totalPages > 1,
    prevDisabled: current <= 1,
    nextDisabled: current >= totalPages,
  }
}

/** Klem een paginanummer binnen [1, totalPages]. */
export function clampPage(n: number, totalPages: number): number {
  return Math.min(Math.max(1, Math.trunc(n)), Math.max(1, totalPages))
}

/**
 * Parse + klem de "ga naar pagina"-invoer. Geeft een pagina binnen
 * [1, totalPages], of `null` bij ongeldige invoer (leeg, niet-numeriek,
 * nul/negatief, decimaal). Een te hoog getal klemt naar de laatste pagina.
 */
export function parsePageInput(raw: string, totalPages: number): number | null {
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 1) return null
  return Math.min(n, Math.max(1, totalPages))
}

/**
 * Bouwt de nieuwe querystring voor een paginawissel: behoudt alle bestaande
 * params en zet/verwijdert `page` (pagina 1 = geen param, schone URL). Pure +
 * testbaar; het component plakt de teruggave achter het pathname.
 */
export function buildPageHref(currentSearch: string, page: number): string {
  const params = new URLSearchParams(currentSearch)
  if (page > 1) params.set('page', String(page))
  else params.delete('page')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * De paginanummers die de desktop-kiezer toont. Bij een getypte query filtert
 * hij op de nummers die met de query beginnen (getypt springen "wint" van
 * scrollen); anders alle pagina's tot `lazyThreshold`, en daarboven een venster
 * rond de huidige pagina + eerste/laatste (opties lazy). Pure + testbaar.
 */
export function pageOptions(
  totalPages: number,
  current: number,
  query: string,
  lazyThreshold = LAZY_OPTIONS_THRESHOLD,
): number[] {
  const q = query.trim()
  if (q !== '') {
    const out: number[] = []
    for (let p = 1; p <= totalPages && out.length < 50; p++) {
      if (String(p).startsWith(q)) out.push(p)
    }
    return out
  }
  if (totalPages <= lazyThreshold) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const set = new Set<number>([1, totalPages])
  for (let p = current - 3; p <= current + 3; p++) {
    if (p >= 1 && p <= totalPages) set.add(p)
  }
  return Array.from(set).sort((a, b) => a - b)
}

// ── Gedeelde control-styling (Streep-tokens + mobiele tap-target) ────────────

const CONTROL_CLASS =
  'rounded-md border border-[#CED0C8] bg-white px-2 py-1 text-[#1A1A18] focus:outline-none focus:ring-2 focus:ring-[#CCFA78] disabled:bg-[#F0F1EC] disabled:text-[#9CA3AF] min-h-11 md:min-h-0 text-base md:text-sm'

// ── Desktop-kiezer: typebare combobox (Popover + cmdk) ───────────────────────

function DesktopPagePicker({
  current,
  totalPages,
  labels,
  disabled,
  onGo,
}: {
  current: number
  totalPages: number
  labels: PaginationLabels
  disabled: boolean
  onGo: (page: number) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  React.useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const options = React.useMemo(
    () => pageOptions(totalPages, current, query),
    [totalPages, current, query],
  )

  const commitTyped = () => {
    const parsed = parsePageInput(query, totalPages)
    if (parsed == null) return
    onGo(parsed)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={labels.goToPage}
          aria-haspopup="listbox"
          className={cn(CONTROL_CLASS, 'inline-flex items-center gap-1 tabular-nums')}
        >
          <span>{current}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#5C5C58]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-28 p-0">
        <Command shouldFilter={false}>
          <div className="border-b border-[#CED0C8] px-2 py-1.5">
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={String(current)}
              inputMode="numeric"
              aria-label={labels.goToPage}
              // Enter met getypte invoer springt naar dat nummer (typen wint);
              // zonder invoer valt Enter door naar cmdk (gemarkeerde optie).
              onKeyDown={(e) => {
                if (e.key === 'Enter' && query.trim() !== '') {
                  e.preventDefault()
                  e.stopPropagation()
                  commitTyped()
                }
              }}
              className="w-full bg-transparent text-base tabular-nums text-[#1A1A18] placeholder-[#9CA3AF] focus:outline-none md:text-sm"
            />
          </div>
          <Command.List className="max-h-56 overflow-y-auto py-1">
            {options.map((p) => (
              <Command.Item
                key={p}
                value={String(p)}
                onSelect={() => {
                  onGo(p)
                  setOpen(false)
                }}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm tabular-nums text-[#1A1A18] data-[selected=true]:bg-[#F0F1EC]"
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', p === current ? 'opacity-100' : 'opacity-0')}
                  aria-hidden="true"
                />
                {p}
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ── Touch-kiezer: native <select> (of number-input bij veel pagina's) ────────

function MobilePagePicker({
  current,
  totalPages,
  labels,
  disabled,
  onGo,
}: {
  current: number
  totalPages: number
  labels: PaginationLabels
  disabled: boolean
  onGo: (page: number) => void
}) {
  const [input, setInput] = React.useState(String(current))
  React.useEffect(() => {
    setInput(String(current))
  }, [current])

  if (totalPages <= MOBILE_SELECT_MAX) {
    return (
      <select
        aria-label={labels.goToPage}
        value={String(current)}
        disabled={disabled}
        onChange={(e) => onGo(Number(e.target.value))}
        className={cn(CONTROL_CLASS, 'tabular-nums')}
      >
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <option key={p} value={String(p)}>
            {p}
          </option>
        ))}
      </select>
    )
  }

  const commit = () => {
    const parsed = parsePageInput(input, totalPages)
    if (parsed == null) {
      setInput(String(current))
      return
    }
    setInput(String(parsed))
    onGo(parsed)
  }

  return (
    <input
      type="number"
      inputMode="numeric"
      min={1}
      max={totalPages}
      aria-label={labels.goToPage}
      value={input}
      disabled={disabled}
      onChange={(e) => setInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
      onBlur={commit}
      className={cn(CONTROL_CLASS, 'w-16 tabular-nums')}
    />
  )
}

// ── Component ────────────────────────────────────────────────────────────────

export interface PaginationProps {
  /** Huidige pagina, 1-based (uit API `meta.page`). */
  page: number
  /** Aantal pagina's — geef dit óf `total` + `pageSize` mee. */
  totalPages?: number
  /** Totaal aantal records (uit API `meta.total`); met `pageSize` -> pagina's. */
  total?: number
  /** Records per pagina (uit API `meta.pageSize`/`meta.limit`). */
  pageSize?: number
  /**
   * Paginawissel-handler (bestaand `navigatePage`/`goToPage` van de lijst, dat
   * URL + filters behoudt). Weggelaten -> het component navigeert zelf via de
   * URL-`page`-param (huidige query blijft behouden).
   */
  onPageChange?: (page: number) => void
  /** Bezig met laden — disablet alle controls tijdens navigatie. */
  pending?: boolean
  /** Extern uitgeschakeld (zelfde effect als `pending`). */
  disabled?: boolean
  /** Optionele itemtelling rechts/onder de balk ("312 artikelen"). */
  totalLabel?: React.ReactNode
  /** Overschrijft de NL-default-teksten (i18n / next-intl). */
  labels?: Partial<PaginationLabels>
  /** Eerste/laatste-knoppen (⏮/⏭), alleen op desktop zichtbaar. Default aan. */
  showFirstLast?: boolean
  /** Optionele pageSize-kiezer; alleen getoond als `onPageSizeChange` er ook is. Default uit. */
  pageSizeOptions?: readonly number[]
  onPageSizeChange?: (size: number) => void
  className?: string
}

export function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  pending = false,
  disabled = false,
  totalLabel,
  labels,
  showFirstLast = true,
  pageSizeOptions,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const router = useRouter()
  const pathname = usePathname()
  const [navPending, startTransition] = React.useTransition()

  const resolved = resolvePaginationLabels(labels)
  const state = paginationState({ total, totalPages, page, pageSize })
  const { current, totalPages: pages, show, prevDisabled, nextDisabled } = state

  // §6 — verbergt zichzelf bij <= 1 pagina (lege lijsten tonen hun empty-state).
  if (!show) return null

  const busy = pending || disabled || navPending

  const go = (target: number) => {
    const next = clampPage(target, pages)
    if (next === current) return
    if (onPageChange) {
      onPageChange(next)
      return
    }
    // Self-nav: lees de huidige query pas bij de klik (client) zodat het component
    // geen `useSearchParams()`-Suspense-grens nodig heeft en veilig in een
    // server-component gerenderd kan worden.
    startTransition(() => {
      const search = typeof window !== 'undefined' ? window.location.search : ''
      router.push(`${pathname}${buildPageHref(search, next)}`)
    })
  }

  return (
    <nav
      aria-label={resolved.nav}
      className={cn(
        'flex flex-wrap items-center justify-center gap-2 text-sm text-[#5C5C58]',
        className,
      )}
    >
      {showFirstLast && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="hidden px-2 md:inline-flex"
          aria-label={resolved.first}
          title={resolved.first}
          disabled={busy || prevDisabled}
          onClick={() => go(1)}
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label={resolved.previous}
        disabled={busy || prevDisabled}
        onClick={() => go(current - 1)}
      >
        {resolved.previous}
      </Button>

      <div className="flex items-center gap-1.5">
        <span>{resolved.page}</span>
        <span className="hidden md:block">
          <DesktopPagePicker
            current={current}
            totalPages={pages}
            labels={resolved}
            disabled={busy}
            onGo={go}
          />
        </span>
        <span className="md:hidden">
          <MobilePagePicker
            current={current}
            totalPages={pages}
            labels={resolved}
            disabled={busy}
            onGo={go}
          />
        </span>
        <span className="tabular-nums">
          {resolved.of} {pages}
        </span>
      </div>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label={resolved.next}
        disabled={busy || nextDisabled}
        onClick={() => go(current + 1)}
      >
        {resolved.next}
      </Button>

      {showFirstLast && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="hidden px-2 md:inline-flex"
          aria-label={resolved.last}
          title={resolved.last}
          disabled={busy || nextDisabled}
          onClick={() => go(pages)}
        >
          <ChevronsRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}

      {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
        <label className="ml-2 flex items-center gap-1.5">
          <span className="sr-only">{resolved.perPage}</span>
          <select
            aria-label={resolved.perPage}
            value={pageSize != null ? String(pageSize) : undefined}
            disabled={busy}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={cn(CONTROL_CLASS, 'tabular-nums')}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={String(size)}>
                {size} {resolved.perPage}
              </option>
            ))}
          </select>
        </label>
      )}

      {totalLabel != null && (
        <span className="w-full text-center md:ml-2 md:w-auto">{totalLabel}</span>
      )}
    </nav>
  )
}
